import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UploadCloud, ShieldCheck, Loader2, Check, Clock, XCircle, FileText, Building2, ArrowRight, Info } from "lucide-react";
import { Card } from "@/components/ui-hud";
import BackToCockpit from "@/components/BackToCockpit";

const DOC_TYPES = [
  { value: "paystub",             label: "Recent pay stub",         hint: "From a 121/135 operator. Redact your SSN — we only need employer + your name." },
  { value: "badge",               label: "Employer crew badge",     hint: "Photo of your badge showing your name + employer + expiry date." },
  { value: "letter_of_employment", label: "Letter of Employment",   hint: "Any signed PDF from HR confirming your active employment." },
  { value: "operator_vouch",      label: "Operator vouch",          hint: "Any verified PJP operator can vouch for you — reach out to them first." },
  { value: "other",               label: "Other verification",      hint: "Any other doc showing you fly cabin. Note what it is below." },
];

export default function VerifyFlightAttendant() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [existing, setExisting] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ doc_type: "paystub", employer_name: "", note: "" });

  useEffect(() => {
    api.get("/verify/flight-attendant/me").then((r) => setExisting(r.data)).catch(() => {});
  }, []);

  if (!user) {
    return (
      <div className="max-w-lg mx-auto px-6 py-24 text-center">
        <p className="text-white/70 mb-4">Sign in to submit FA verification.</p>
        <Link to="/login" className="btn-hud btn-hud-solid">SIGN IN</Link>
      </div>
    );
  }
  if (user.role !== "flight_attendant") {
    return (
      <div className="max-w-lg mx-auto px-6 py-24 text-center">
        <p className="text-white/70">This page is for Flight Attendants only. Your role is <b>{user.role}</b>.</p>
        <Link to="/profile" className="btn-hud mt-4">EDIT PROFILE</Link>
      </div>
    );
  }

  const status = existing?.status || "not_required";
  const sub = existing?.submission;

  const uploadFile = async () => {
    if (!file) return null;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "fa_verification");
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return r.data.id;
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Attach at least one document.");
    if (!form.employer_name.trim()) return toast.error("Employer name is required.");
    setSubmitting(true);
    try {
      const file_id = await uploadFile();
      if (!file_id) throw new Error("Upload failed");
      await api.post("/verify/flight-attendant", { ...form, file_id });
      toast.success("Submitted! We'll review within 24-48h.");
      await refresh();
      const fresh = await api.get("/verify/flight-attendant/me");
      setExisting(fresh.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Already approved? Send them home.
  if (status === "verified") {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16">
        <Card>
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="h-8 w-8 text-avail" />
            <div>
              <div className="overline text-avail">VERIFIED CREW</div>
              <div className="font-display text-2xl font-bold">You're set.</div>
            </div>
          </div>
          <p className="text-white/70 mb-6">
            Your Flight Attendant verification is approved. The verified badge is now on your profile and JumpSeat access is unlocked
            once you're on PJP Pro.
          </p>
          <div className="flex gap-3">
            <Link to="/dashboard" className="btn-hud btn-hud-solid">GO TO COCKPIT <ArrowRight className="h-3.5 w-3.5" /></Link>
            <Link to="/membership/addon?tier=pro" className="btn-hud">Upgrade to Pro</Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-14">
      <BackToCockpit />
      <div className="overline text-orange-hud mb-2">// FLIGHT ATTENDANT · VERIFICATION</div>
      <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter mb-3">Verify your crew status.</h1>
      <p className="text-white/70 mb-8 max-w-xl">
        We manually verify Flight Attendants because there's no public FAA registry for cabin crew.
        Upload ONE piece of employer documentation and we'll approve within <b className="text-cyan">24-48 hours</b>.
      </p>

      {status === "submitted" && sub && (
        <Card className="!border-cyan/40 mb-6" data-testid="fa-verify-pending-card">
          <div className="flex items-center gap-3 mb-3">
            <Clock className="h-5 w-5 text-cyan animate-pulse" />
            <div className="font-display text-lg font-semibold">Under review</div>
          </div>
          <p className="text-white/70 text-sm mb-3">
            We received your <b className="text-white">{DOC_TYPES.find((d) => d.value === sub.doc_type)?.label || sub.doc_type}</b> for
            <b className="text-white"> {sub.employer_name}</b>. You'll get an email when it's reviewed — usually within a business day.
          </p>
          <div className="mono text-[10px] tracking-widest uppercase text-white/45">
            Submitted {new Date(sub.created_at).toLocaleString()}
          </div>
        </Card>
      )}
      {status === "rejected" && sub && (
        <Card className="!border-red-500/40 mb-6" data-testid="fa-verify-rejected-card">
          <div className="flex items-center gap-3 mb-3">
            <XCircle className="h-5 w-5 text-red-400" />
            <div className="font-display text-lg font-semibold">Not verified</div>
          </div>
          <p className="text-white/70 text-sm mb-3">
            Your last submission wasn't accepted{sub.review_note ? `: "${sub.review_note}"` : "."}. Re-upload a stronger doc below and we'll re-review.
          </p>
        </Card>
      )}

      {(status === "pending" || status === "rejected" || status === "not_required") && (
        <Card>
          <form onSubmit={submit} className="space-y-5" data-testid="fa-verify-form">
            <div>
              <label className="block text-[11px] uppercase tracking-widest text-white/50 mb-2">Type of document</label>
              <div className="grid sm:grid-cols-2 gap-2" data-testid="fa-verify-doctype-picker">
                {DOC_TYPES.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => setForm({ ...form, doc_type: d.value })}
                    data-testid={`fa-verify-doctype-${d.value}`}
                    className={`text-left p-3 rounded-md border transition-colors ${form.doc_type === d.value ? "border-cyan bg-cyan/[0.08]" : "border-white/10 hover:border-white/25"}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className={`h-4 w-4 ${form.doc_type === d.value ? "text-cyan" : "text-white/40"}`} />
                      <div className="font-display font-semibold text-sm text-white">{d.label}</div>
                    </div>
                    <div className="text-[11px] text-white/55 leading-snug">{d.hint}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-widest text-white/50 mb-2 flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5" /> Your employer
              </label>
              <input
                type="text"
                required
                data-testid="fa-verify-employer-name"
                value={form.employer_name}
                onChange={(e) => setForm({ ...form, employer_name: e.target.value })}
                placeholder="e.g. NetJets, Wheels Up, Executive Fliteways"
                className="input-hud w-full"
              />
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-widest text-white/50 mb-2">Upload document</label>
              <label
                data-testid="fa-verify-file-picker"
                className="flex items-center justify-center gap-3 border-2 border-dashed border-white/15 hover:border-cyan/40 hover:bg-cyan/[0.03] rounded-lg py-8 cursor-pointer transition-colors"
              >
                <UploadCloud className="h-5 w-5 text-white/50" />
                <div className="text-sm text-white/70">
                  {file ? file.name : "Click to attach a PDF or image (max 25MB)"}
                </div>
                <input
                  type="file"
                  accept=".pdf,image/*"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files[0] || null)}
                />
              </label>
              <div className="mt-2 flex items-start gap-1.5 text-[11px] text-white/45">
                <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                <span>Redact your SSN if visible. We only need your name + employer + doc type.</span>
              </div>
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-widest text-white/50 mb-2">Anything else? <span className="text-white/30">(optional)</span></label>
              <textarea
                data-testid="fa-verify-note"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="If it's an operator vouch, tell us who's vouching. Otherwise, leave blank."
                rows={2}
                className="input-hud w-full"
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-white/[0.06]">
              <div className="mono text-[10px] tracking-widest uppercase text-white/45">
                Reviewed by hand · usually &lt; 24h
              </div>
              <button
                type="submit"
                disabled={submitting || uploading || !file}
                data-testid="fa-verify-submit"
                className="btn-hud btn-hud-solid"
              >
                {(submitting || uploading) ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4" /> SUBMIT FOR REVIEW</>}
              </button>
            </div>
          </form>
        </Card>
      )}
    </div>
  );
}
